import axios from '../services/axiosConfig';

// Use environment variable for API URL (consistent with CustomReports pattern)
const API_URL = process.env.REACT_APP_API_URL;

// Helper function to get auth token (same pattern as authService)
const acquireTokenForAPI = async () => {
    // First try to get token from cookie
    const getCookie = (name) => {
        const value = `; ${document.cookie}`;
        const parts = value.split(`; ${name}=`);
        if (parts.length === 2) return parts.pop().split(';').shift();
        return null;
    };
    
    // Try cookie first
    const cookieToken = getCookie('auth_token');
    if (cookieToken) {
        console.log('Using auth token from cookie');
        return cookieToken;
    }
    
    // Fall back to session storage
    const sessionToken = sessionStorage.getItem('authToken');
    if (sessionToken) {
        console.log('Using auth token from session storage');
        return sessionToken;
    }
    
    console.warn('No auth token found in cookie or session storage');
    return null;
};

// Global caching for ReportList API calls
let reportsApiCalled = false;
let reportsApiResponses = {};

/**
 * Get reports directly without any caching (for debugging)
 * @param {number|string} userId - User ID for authorization and filtering
 * @param {string} adminUser - Flag indicating if user is an admin
 * @param {number} menuId - Optional menuId/tabId for filtering reports
 * @returns {Promise} Promise object representing the list of reports
 */
export const getReportsDirect = async (userId, adminUser, menuId) => {
  try {
    console.log('getReportsDirect called with params:', { userId, adminUser, menuId });
    
    // Try both lowercase and uppercase parameter variations
    const paramVariations = [
      { userId: userId, adminUser: adminUser, menuId: menuId },
      { UserId: userId, AdminUser: adminUser, menuId: menuId },
      { userId: userId, adminUser: adminUser, MenuId: menuId }
    ];
    
    // Get authentication token from both API and cookies
    const token = await acquireTokenForAPI();
    
    // Get auth token from cookies
    const getAuthTokenFromCookie = () => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; auth_token=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    };
    
    // Get the auth token from cookie
    const cookieToken = getAuthTokenFromCookie();
    console.log('Auth tokens available - Session:', !!token, 'Cookie:', !!cookieToken);
    
    // Use cookie token if available, otherwise use session token
    const authToken = cookieToken || token;
    
    if (!authToken) {
      console.warn('No auth token available');
    }
    
    // Try each parameter variation with both axios and fetch
    for (const params of paramVariations) {
      const urlParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          urlParams.append(key, value);
        }
      });
      
      const apiUrl = `${API_URL}/ReportList?${urlParams.toString()}`;
      console.log(`Trying ReportList API call with URL: ${apiUrl}`);
      
      // Try with axios first
      try {
        const response = await axios.get(apiUrl, {
          headers: {
            'Authorization': authToken ? `Bearer ${authToken}` : '',
            'Cache-Control': 'no-cache'
          },
          timeout: 10000 // 10 second timeout
        });
        
        console.log(`ReportList API axios response (${urlParams.toString()}):`, response.status, response.data);
        
        if (response.data) {
          if (response.data.Reports || response.data.reports) {
            return {
              reports: response.data.Reports || response.data.reports || [],
              showKeywordTags: response.data.ShowKeywordTags || response.data.showKeywordTags || true
            };
          }
        }
      } catch (axiosError) {
        console.warn(`Axios attempt failed for ${urlParams.toString()}:`, axiosError.message);
      }
      
      // Try with fetch as fallback
      try {
        const fetchResponse = await fetch(apiUrl, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Authorization': authToken ? `Bearer ${authToken}` : '',
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache'
          }
        });
        
        console.log(`ReportList API fetch status (${urlParams.toString()}):`, fetchResponse.status);
        
        if (fetchResponse.ok) {
          const fetchData = await fetchResponse.json();
          console.log(`ReportList API fetch data (${urlParams.toString()}):`, fetchData);
          
          if (fetchData) {
            return {
              reports: fetchData.Reports || fetchData.reports || [],
              showKeywordTags: fetchData.ShowKeywordTags || fetchData.showKeywordTags || true
            };
          }
        }
      } catch (fetchError) {
        console.warn(`Fetch attempt failed for ${urlParams.toString()}:`, fetchError.message);
      }
    }
    
    // If we've tried all variations and still nothing, return empty result
    console.warn('All ReportList API attempts failed, returning empty result');
    return { reports: [], showKeywordTags: true, error: 'All API attempts failed' };
  } catch (error) {
    console.error('Error in direct ReportList API call:', error);
    return { reports: [], showKeywordTags: true, error: error.message };
  }
};

/**
 * Get all available reports
 * @param {number} userId - User ID for authorization and filtering (from session)
 * @param {string} adminUser - Flag indicating if user is an admin (defaults to "false")
 * @param {number} menuId - Optional menuId/tabId for filtering reports
 * @returns {Promise} Promise object representing the list of reports
 */
export const getReports = async (userId, adminUser, menuId) => {
  try {
    // Check if we have Main API response data with userId and menuId
    try {
      const mainApiResponseStr = sessionStorage.getItem('mainApiResponse');
      if (mainApiResponseStr) {
        const mainApiResponse = JSON.parse(mainApiResponseStr);
        // Use userId from Main API response if available
        if (mainApiResponse?.userId || mainApiResponse?.UserId) {
          userId = mainApiResponse?.userId || mainApiResponse?.UserId;
          console.log('Using userId from Main API response in getReports:', userId);
        }
      }
    } catch (parseError) {
      console.warn('Error parsing Main API response from session storage:', parseError);
    }
    
    // Always make a fresh API call to ensure we get the latest data
    console.log('Making fresh API call to ReportList endpoint with menuId:', menuId);
    
    // Call the direct implementation without caching
    return await getReportsDirect(userId, adminUser, menuId);
    
  } catch (error) {
    // For production, throw the error or return empty reports
    const errorResult = { reports: [], showKeywordTags: true, error: error.message };
    
    // Don't cache error results
    return errorResult;
  }
};

// Helper function to save reports data to cache
function saveToReportsCache(userId, adminUser, menuId, data) {
  const cacheKey = `${userId}-${adminUser}-${menuId || 0}`;
  
  // Set the global cache
  reportsApiCalled = true;
  reportsApiResponses[cacheKey] = data;
  
  // Also save to session storage for persistence
  try {
    sessionStorage.setItem(`reports_cache_${cacheKey}`, JSON.stringify(data));
  } catch (storageError) {
    console.warn('Could not store reports in session storage:', storageError);
  }
}

/**
 * Get report parameter validations
 * @returns {Promise} Promise object representing the report parameter validations
 */
export const getReportParameterValidations = async () => {
  try {
    const token = await acquireTokenForAPI();
    if (!token) {
        throw new Error('No access token available');
    }
    
    const response = await axios.get(`${API_URL}/ReportList/parameter-validations`, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    console.error('Error fetching report parameter validations:', error);
    throw error;
  }
};

/**
 * Get report details by ID
 * @param {number} reportId - ID of the report to fetch
 * @param {number} menuId - MenuId for filtering permissions
 * @param {number} userId - User ID for authorization (from session)
 * @param {Object} parameters - Optional parameters for the report (defaults to null)
 * @returns {Promise} Promise object representing the report details
 */
export const getReportById = async (reportId, menuId, userId, parameters = null) => {
  try {
    // Format the request body according to the specified structure
    // This follows the pattern: { reportId: "3", menuId: 1, parameters: { additionalProp1: "string", ... } }
    const requestBody = {
      reportId: reportId.toString(),
      menuId: menuId,
      UserID: userId
    };
    
    // Only add parameters if they're provided and not empty
    if (parameters && Object.keys(parameters).length > 0) {
      requestBody.parameters = parameters;
    }

    // Check session storage for cached report data - use parameters in cache key if provided
    const paramStr = parameters ? `-${JSON.stringify(parameters)}` : '';
    const cachedReportKey = `report_${reportId}_${menuId}${paramStr}`;
    const cachedReport = sessionStorage.getItem(cachedReportKey);
    
    if (cachedReport) {
      try {
        const reportData = JSON.parse(cachedReport);
        return reportData;
      } catch (cacheError) {
        console.warn('Error parsing cached report data:', cacheError);
      }
    }

    const token = await acquireTokenForAPI();
    if (!token) {
        throw new Error('No access token available');
    }

    const params = {
      ReportId: reportId,
      MenuId: menuId,
      UserID: userId,
      parameters: parameters || {}
    };

    const response = await axios.post(`${API_URL}/ViewReport/GetReportDetails`, params, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });

    const data = response.data;
    
    // The API returns { ReportDetails, ReportParameters } structure, so we need to combine these
    if (data && data.ReportDetails) {
      const { ReportDetails, ReportParameters } = data;
      
      // Transform the response data into the format expected by the UI
      const reportData = {
        rptId: ReportDetails.RptId,
        rptName: ReportDetails.RptName,
        rptDesr: ReportDetails.RptDesr,
        rptPath: ReportDetails.RptPath,
        rptServer: ReportDetails.RptServer,
        // Map the parameters to the format expected by the UI
        parameters: ReportParameters?.map(param => ({
          name: param.Name,
          prompt: param.Label,
          type: param.ParameterType, 
          isRequired: param.IsRequired,
          defaultValue: param.DefaultValue || '',
          availableValues: param.ValidValues?.map(v => ({
            value: v.Value,
            label: v.Label
          })) || []
        })) || [],
        // Map any dependencies between parameters
        dependencies: ReportParameters?.filter(p => p.DependsOn?.length)
          .map(p => `${p.DependsOn.join(',')}:${p.Name}`)
          .join(';') || ''
      };
      
      // Cache the report data in session storage
      try {
        sessionStorage.setItem(cachedReportKey, JSON.stringify(reportData));
      } catch (storageError) {
        console.warn('Failed to cache report data:', storageError);
      }
      
      return reportData;
    } else {
      throw new Error('Invalid response format from API');
    }
  } catch (error) {
    throw error;
  }
};

/**
 * Execute a report with the provided parameters
 * @param {number} reportId - ID of the report to execute 
 * @param {number} menuId - MenuId for permissions
 * @param {Object} parameters - Parameters for the report
 * @param {number} userId - User ID for authorization (from session)
 * @returns {Promise} Promise object representing the execution result
 */
export const executeReport = async (reportId, menuId, parameters, userId) => {
  try {
    const token = await acquireTokenForAPI();
    if (!token) {
        throw new Error('No access token available');
    }

    // Format the request body according to the API requirements
    const requestBody = {
      reportId: reportId.toString(),
      menuId: menuId, 
      UserID: userId, // Using the provided userId parameter
      parameters: parameters || {}
    };
    
    const response = await axios.post(`${API_URL}/ViewReport/ExecuteReport`, requestBody, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Download a report in the specified format
 * @param {number} reportId - ID of the report to download
 * @param {string} format - Format to export (PDF, Excel, Word, etc.)
 * @param {object} parameters - Key/value pairs of parameters
 * @param {number} menuId - ID of the menu for permission context
 * @param {number} userId - User ID for authorization (from session)
 * @returns {Promise} Promise object representing the response with savedReportName
 */
export const downloadReport = async (reportId, format, parameters = {}, menuId, userId) => {
  try {
    const token = await acquireTokenForAPI();
    if (!token) {
        throw new Error('No access token available');
    }
    
    // Create a copy of parameters to avoid modifying the original
    const exportParameters = { ...parameters };
    
    // Check if savedReportName exists in parameters and store it separately
    const savedReportName = exportParameters.savedReportName;
    
    // If savedReportName exists, use the direct download endpoint
    if (savedReportName) {
      // Remove it from the parameters to avoid sending it twice
      delete exportParameters.savedReportName;
      
      // Call the download endpoint with savedReportName and reportId
      const response = await axios.get(
        `${API_URL}/DownloadReport/download`, 
        { 
          params: { 
            reportname: savedReportName,
            rptid: reportId
          },
          responseType: 'blob',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      return response.data;
    }
    
    // Remove the Format field from parameters since it's already included as a top-level parameter
    delete exportParameters["Format"];
    
    // Normalize the format according to backend expectations
    let normalizedFormat = format;
    
    // Convert user-friendly format names to backend format types
    if (format && typeof format === 'string') {
      const formatLower = format.toLowerCase();
      switch (formatLower) {
        case "excel":
          normalizedFormat = "xlsx";
          break;
        case "pdf":
          normalizedFormat = "pdf";
          break;
        case "word":
          normalizedFormat = "docx";
          break;
        case "csv":
          normalizedFormat = "csv";
          break;
        case "xml":
          normalizedFormat = "xml";
          break;
        // Keep the original format if it's already in the right format
        case "xlsx":
        case "docx":
          normalizedFormat = formatLower;
          break;
        default:
          console.warn(`Unknown format: ${format}, defaulting to xlsx`);
          normalizedFormat = "xlsx";
          break;
      }
    } else {
      // Default to Excel if no format specified
      normalizedFormat = "xlsx";
    }
    
    // Format the request body according to the API requirements
    const requestBody = {
      reportId: reportId.toString(),
      menuId: menuId, 
      Format: normalizedFormat.toLowerCase(),
      UserID: userId, 
      parameters: exportParameters
    };
    
    const response = await axios.post(
      `${API_URL}/ViewReport/ExportReport`,
      requestBody,
      {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      }
    );
    
    // Check if the response contains savedReportName and return it
    return response.data && response.data.savedReportName 
      ? { savedReportName: response.data.savedReportName } 
      : response.data;
  } catch (error) {
    console.error(`Error downloading report ${reportId} as ${format}:`, error);
    throw error;
  }
};

/**
 * Export a report to Excel format
 * @param {number} reportId - ID of the report to download
 * @param {number} menuId - ID of the menu for permission context
 * @param {string} format - Format to export the Excel data in (usually PDF)
 * @param {object} parameters - Key/value pairs of parameters
 * @param {number} userId - User ID for authorization (from session)
 * @returns {Promise} Promise object representing the report blob with fileExtension property
 */
export const exportToExcel = async (reportId, menuId, format = "pdf", parameters = {}, userId, adminUser = 'false') => {
  try {
    // Get auth token from various sources, with proper fallbacks
    const getCookieToken = () => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; auth_token=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      
      // Also check for other common token cookie names
      const altParts = value.split(`; token=`);
      if (altParts.length === 2) return altParts.pop().split(';').shift();
      
      // Check for JWT cookie
      const jwtParts = value.split(`; jwt=`);
      if (jwtParts.length === 2) return jwtParts.pop().split(';').shift();
      
      return null;
    };
    
    // Try multiple sources for the token
    // 1. First try cookie
    let token = getCookieToken();
    console.log('ExportToExcel: Cookie token available:', !!token);
    
    // 2. Then try session storage
    if (!token) {
      token = sessionStorage.getItem('authToken');
      console.log('ExportToExcel: Session storage token available:', !!token);
    }
    
    // 3. Then try localStorage
    if (!token) {
      token = localStorage.getItem('authToken') || localStorage.getItem('token') || localStorage.getItem('jwt');
      console.log('ExportToExcel: Local storage token available:', !!token);
    }
    
    // 4. Finally try the acquireTokenForAPI function
    if (!token) {
      try {
        // Import auth service dynamically to avoid circular dependencies
        const authModule = await import('../services/authService');
        if (authModule && authModule.authService) {
          token = await authModule.authService.acquireTokenForAPI();
          console.log('ExportToExcel: Auth service token available:', !!token);
        }
      } catch (authError) {
        console.warn('Error getting token from authService:', authError);
      }
    }
    
    console.log('ExportToExcel: Final token status:', !!token);
    
    // If no token found but cookies are enabled, mark this as cookie-based auth
    if (!token && navigator.cookieEnabled) {
      console.log('ExportToExcel: No token found but cookies are enabled, using cookie-based auth');
      token = 'cookie-based-auth';
    }
    
    if (!token) {
      console.error('No auth token available from any source');
      throw new Error('No access token available');
    }
    
    // Create a copy of parameters to avoid modifying the original
    const exportParameters = { ...parameters };
    
    // Remove the Format field from parameters since it's already included as a top-level parameter
    delete exportParameters["Format"];
    
    // Format the request body according to the API requirements
    const requestBody = {
      reportId: reportId.toString(),
      menuId: menuId,
      Format: format,
      UserID: userId,
      AdminUser: adminUser,
      parameters: exportParameters
    };
    
    console.log('ExportToExcel: Making API call with body:', requestBody);
    
    // Log the full request details for debugging
    console.log('ExportToExcel: Full request details:', {
      url: `${API_URL}/ViewReport/ExportToExcel`,
      body: requestBody,
      token: token ? (token.length > 10 ? token.substring(0, 5) + '...' + token.substring(token.length - 5) : token) : 'null'
    });
    
    // Set up request options
    const requestOptions = {
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, text/csv',
        'Content-Type': 'application/json'
      },
      responseType: 'blob',
      withCredentials: true // Include cookies in the request
    };
    
    // Add Authorization header if token doesn't appear to be a cookie-based auth token
    if (token && token !== 'cookie-based-auth') {
      // Try both with and without Bearer prefix
      if (token.startsWith('Bearer ')) {
        requestOptions.headers['Authorization'] = token;
      } else {
        requestOptions.headers['Authorization'] = `Bearer ${token}`;
      }
      
      // Also store in session storage in case the API reads it from there
      sessionStorage.setItem('authToken', token);
    }
    
    // Add token as URL parameter as an alternative method (some APIs require this)
    let url = `${API_URL}/ViewReport/ExportToExcel`;
    if (token && token !== 'cookie-based-auth' && !token.startsWith('Bearer ')) {
      url += url.includes('?') ? `&token=${encodeURIComponent(token)}` : `?token=${encodeURIComponent(token)}`;
    }
    
    // Client-side download logic with enhanced error handling
    const response = await axios.post(url, requestBody, requestOptions);

    const responseBlob = response.data;
  
    // Check the content type to determine the appropriate file extension
    const contentType = response.headers['content-type'] || response.headers.get?.('content-type');
    let fileExtension = '.xlsx'; // Default to .xlsx
    
    if (contentType) {
      if (contentType.includes('text/csv') || contentType.includes('csv')) {
        fileExtension = '.csv';
      } else if (contentType.includes('spreadsheetml') || contentType.includes('excel')) {
        fileExtension = '.xlsx';
      }
    }
    
    // Create a new blob with the detected file type
    const blob = new Blob([responseBlob], { 
      type: contentType || (fileExtension === '.csv' 
        ? 'text/csv' 
        : 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet')
    });
    
    // Add the fileExtension property to the blob
    blob.fileExtension = fileExtension;
    
    return blob;
  } catch (error) {
    console.error(`Error exporting report ${reportId} to Excel:`, error);
    
    // Enhanced error logging for better debugging
    let errorMessage = 'Failed to export report';
    
    if (error.response) {
      // The request was made and the server responded with a status code
      // that falls out of the range of 2xx
      console.error('Error response:', {
        status: error.response.status,
        statusText: error.response.statusText,
        data: error.response.data
      });
      
      errorMessage = `Server error: ${error.response.status} ${error.response.statusText}`;
      
      // If we have a blob response with error details, read it
      if (error.response.data instanceof Blob) {
        try {
          const reader = new FileReader();
          reader.onload = () => {
            try {
              const errorData = JSON.parse(reader.result);
              console.error('Error response data:', errorData);
            } catch (e) {
              console.error('Error response text:', reader.result);
            }
          };
          reader.readAsText(error.response.data);
        } catch (e) {
          console.error('Error parsing error response blob:', e);
        }
      }
    } else if (error.request) {
      // The request was made but no response was received
      console.error('No response received:', error.request);
      errorMessage = 'No response from server';
    } else {
      // Something happened in setting up the request that triggered an Error
      console.error('Request setup error:', error.message);
      errorMessage = `Request error: ${error.message}`;
    }
    
    throw new Error(errorMessage);
  }
};

/**
 * Get cascading parameter values
 * @param {number} reportId - ID of the report
 * @param {string} paramName - Name of the parent parameter that changed
 * @param {string} paramValue - Value of the parent parameter
 * @param {Array} dependentParams - Array of dependent parameter names
 * @returns {Promise} Promise object representing the dependent parameter values
 */
export const getCascadingParameterValues = async (reportId, paramName, paramValue, dependentParams) => {
  try {
    const token = await acquireTokenForAPI();
    if (!token) {
        throw new Error('No access token available');
    }
    
    const response = await axios.post(`${API_URL}/reports/${reportId}/parameters/cascade`, {
      paramName, 
      paramValue,
      dependentParams
    }, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    throw error;
  }
};

/**
 * Clear report parameter session values
 * @returns {Promise} Promise object representing the operation result
 */
export const clearParameterSessionValues = async () => {
  try {
    const token = await acquireTokenForAPI();
    if (!token) {
        throw new Error('No access token available');
    }
    
    const response = await axios.post(`${API_URL}/reports/parameters/clear`, {}, {
      headers: {
        'Authorization': `Bearer ${token}`
      }
    });
    
    return response.data;
  } catch (error) {
    throw error;
  }
};
/**
 * Fetch the reports list directly using fetch API as a last resort
 * This is a simpler method with minimal code that should work if all else fails
 */
export const fetchReportsListDirect = async (userId, adminUser, menuId) => {
  try {
    // Try to get values from Main API response first
    try {
      const mainApiResponseStr = sessionStorage.getItem('mainApiResponse');
      if (mainApiResponseStr) {
        const mainApiResponse = JSON.parse(mainApiResponseStr);
        // Use userId from Main API response if available
        if (mainApiResponse?.userId || mainApiResponse?.UserId) {
          userId = mainApiResponse?.userId || mainApiResponse?.UserId;
          console.log('Using userId from Main API response in fetchReportsListDirect:', userId);
        }
        // Use menuId from Main API response if available
        if (mainApiResponse?.menuId || mainApiResponse?.MenuId) {
          menuId = mainApiResponse?.menuId || mainApiResponse?.MenuId;
          console.log('Using menuId from Main API response in fetchReportsListDirect:', menuId);
        }
      }
    } catch (parseError) {
      console.warn('Error parsing Main API response from session storage:', parseError);
    }
    
    console.log('Last resort: fetchReportsListDirect with params:', { userId, adminUser, menuId });
    
    // Get auth token from cookies
    const getAuthTokenFromCookie = () => {
      const value = `; ${document.cookie}`;
      const parts = value.split(`; auth_token=`);
      if (parts.length === 2) return parts.pop().split(';').shift();
      return null;
    };
    
    // Get the auth token from cookie
    const authToken = getAuthTokenFromCookie();
    console.log('Auth token from cookie available:', !!authToken);
    
    // Try with all possible parameter combinations
    const urls = [
      `${API_URL}/ReportList?userId=${userId}&adminUser=${adminUser}&menuId=${menuId}`,
      `${API_URL}/ReportList?UserId=${userId}&AdminUser=${adminUser}&menuId=${menuId}`,
      `${API_URL}/ReportList?userId=${userId}&adminUser=${adminUser}`, // without menuId
      `${API_URL}/ReportList?UserId=${userId}&AdminUser=${adminUser}` // without menuId, uppercase params
    ];
    
    // Try each URL
    for (const url of urls) {
      try {
        console.log(`Trying direct fetch to ${url}`);
        const response = await fetch(url, {
          method: 'GET',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Cache-Control': 'no-cache',
            'Authorization': authToken ? `Bearer ${authToken}` : ''
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          console.log('fetchReportsListDirect success:', data);
          return {
            reports: data.Reports || data.reports || [],
            showKeywordTags: data.ShowKeywordTags || data.showKeywordTags || true
          };
        } else {
          console.warn(`fetchReportsListDirect got status ${response.status} for ${url}`);
        }
      } catch (err) {
        console.warn(`fetchReportsListDirect error for ${url}:`, err.message);
      }
    }
    
    return { reports: [], showKeywordTags: true, error: 'All direct fetch attempts failed' };
  } catch (error) {
    console.error('fetchReportsListDirect error:', error);
    return { reports: [], showKeywordTags: true, error: error.message };
  }
};

// View a report as 
export const viewReportAsPDF = async (reportId, menuId, userId) => {
  try {
    const token = await acquireTokenForAPI();
    if (!token) {
        throw new Error('No access token available');
    }

    const requestData = {
      reportId: reportId.toString(),
      menuId: menuId,
      UserID: userId
    };
    
    const response = await axios.post(`${API_URL}/ViewReport/ViewAsPDF`, requestData, {
      headers: {
        'Authorization': `Bearer ${token}`
      },
      responseType: 'blob'
    });
    
    return response.data;
  } catch (error) {
    console.error(`Error viewing report ${reportId} as PDF:`, error);
    throw error;
  }
};

/**
 * Clear the reports cache in session storage
 * This function is used when reports need to be refreshed from the server
 * @param {number} menuId - Optional menuId to only clear cache for specific menu
 * @returns {boolean} True if cache was cleared successfully
 */
export const clearReportsCache = (menuId) => {
  try {
    // Reset the global cache flags
    reportsApiCalled = false;
    reportsApiResponses = {};
    
    console.log('🧹 Clearing reports cache for menuId:', menuId);
    
    // Find and remove all report-related cache items from sessionStorage
    Object.keys(sessionStorage).forEach(key => {
      // If menuId is provided, only clear cache for that menu
      if (menuId !== undefined && menuId !== null && key.includes(`reports_cache_`)) {
        const keyParts = key.split('_');
        const cacheKeyWithMenuId = keyParts[keyParts.length - 1];
        const menuIdInKey = cacheKeyWithMenuId.split('-')[2]; // Extract menuId from userId-adminUser-menuId format
        
        if (menuIdInKey === menuId.toString()) {
          console.log('🗑️ Removing cache key:', key);
          sessionStorage.removeItem(key);
        }
      } 
      // Otherwise clear all report caches
      else if (key.startsWith('report_') || key.startsWith('reports_cache_')) {
        console.log('🗑️ Removing cache key:', key);
        sessionStorage.removeItem(key);
      }
    });
    
    return true;
  } catch (error) {
    console.error('Error clearing reports cache:', error);
    return false;
  }
};
